import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import styles from './AccountSettings.module.css';
import { UserIcon, LogOutIcon, EyeIcon, EyeOffIcon, LightbulbIcon, TrashIcon, CheckIcon, XIcon } from './Icons';
import { useAutoSave } from '../hooks/useAutoSave';

const AccountSettings = () => {
  const { user, isAuthenticated, logout, getAccessTokenSilently } = useAuth0();
  const { 
    createBackup, 
    isAuthenticated: isAuth, 
    user: userData, 
    userInfo, 
    updateUserData, 
    syncUserData,
    exportUserData
  } = useAutoSave();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [userProfile, setUserProfile] = useState({});
  const [userRole, setUserRole] = useState('');
  const [availableRoles, setAvailableRoles] = useState([]);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [validation, setValidation] = useState({});

  useEffect(() => {
    loadUserData();
    loadUserRole();
    // Set active tab based on URL hash
    const hash = window.location.hash.replace('#', '');
    if (hash && ['profile', 'security', 'data', 'role'].includes(hash)) {
      setActiveTab(hash);
    }
  }, [window.location.hash]);

  const loadUserData = () => {
    // Load from localStorage
    const savedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    // If we have user info from AuthContext, merge it
    if (userData) {
      const mergedProfile = {
        ...savedProfile,
        fullName: savedProfile.fullName || userData.name || ((userData.given_name ? userData.given_name : '') + (userData.family_name ? ' ' + userData.family_name : '')),
        email: savedProfile.email || userData.email || '',
      };
      // Remove 'undefined' from fullName if present
      mergedProfile.fullName = (mergedProfile.fullName || '').replace(/undefined/g, '').replace(/  +/g, ' ').trim();
      setUserProfile(mergedProfile);
    } else {
      setUserProfile(savedProfile);
    }
  };

  const loadUserRole = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://main.quila.app'}/api/role-selection/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.user && data.user.role) {
          setUserRole(data.user.role);
        }
      }
    } catch (error) {
      console.log('Could not load user role:', error);
    }
    
    // Set available roles
    setAvailableRoles([
      { value: 'student', label: 'Student' },
      { value: 'counselor', label: 'Counselor' },
      { value: 'district_student', label: 'District Student' },
      { value: 'district_admin', label: 'District Admin' }
    ]);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'fullName':
        if (!value.trim()) return 'Full name is required';
        if (value.trim().length < 2) return 'Full name must be at least 2 characters';
        break;
      case 'email':
        if (!value.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Please enter a valid email address';
        break;
      case 'phone':
        if (value && value.trim()) {
          const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
          const cleanPhone = value.replace(/[\s\-\(\)]/g, '');
          if (!phoneRegex.test(cleanPhone)) return 'Please enter a valid phone number';
        }
        break;
      case 'highSchool':
        if (!value.trim()) return 'High school name is required';
        break;
      case 'graduationYear':
        if (!value) return 'Graduation year is required';
        const currentYear = new Date().getFullYear();
        const selectedYear = parseInt(value);
        if (selectedYear < currentYear || selectedYear > currentYear + 5) {
          return 'Please select a valid graduation year';
        }
        break;
      default:
        return null;
    }
    return null;
  };

  const handleProfileChange = (field, value) => {
    const error = validateField(field, value);
    setValidation(prev => ({
      ...prev,
      [field]: error
    }));

    setUserProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRoleChange = async (newRole) => {
    if (newRole === userRole) return;
    
    setIsChangingRole(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'https://main.quila.app'}/api/role-selection/select-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          role: newRole,
          name: userProfile.fullName || user?.name || 'User',
          email: userProfile.email || user?.email || ''
        })
      });

      if (response.ok) {
        setUserRole(newRole);
        setMessage({ type: 'success', text: `Role changed to ${newRole} successfully!` });
        
        // If changing to counselor, redirect to verification
        if (newRole === 'counselor') {
          setTimeout(() => {
            window.location.href = '/counselor-verification';
          }, 2000);
        }
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to change role' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to change role. Please try again.' });
    } finally {
      setIsChangingRole(false);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    
    // Validate all fields
    const errors = {};
    const requiredFields = ['fullName', 'email', 'highSchool', 'graduationYear'];
    
    requiredFields.forEach(field => {
      const error = validateField(field, userProfile[field]);
      if (error) errors[field] = error;
    });

    if (Object.keys(errors).length > 0) {
      setValidation(errors);
      showMessage('error', 'Please fix the errors before saving');
      setIsLoading(false);
      return;
    }

    try {
      // Save to localStorage
      localStorage.setItem('userProfile', JSON.stringify(userProfile));
      
      // Update consolidated user data
      updateUserData('userProfile', userProfile);
      
      // Sync data
      syncUserData();
      
      // Dispatch event to notify other components
      window.dispatchEvent(new Event('userProfileUpdated'));
      
      showMessage('success', 'Profile updated successfully!');
      
      // Clear validation errors
      setValidation({});
    } catch (error) {
      console.error('Error saving profile:', error);
      showMessage('error', 'Failed to save profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!security.currentPassword) {
      showMessage('error', 'Current password is required');
      return;
    }
    
    if (security.newPassword !== security.confirmPassword) {
      showMessage('error', 'New passwords do not match');
      return;
    }
    
    if (security.newPassword.length < 8) {
      showMessage('error', 'Password must be at least 8 characters long');
      return;
    }

    // Password strength validation
    const hasUpperCase = /[A-Z]/.test(security.newPassword);
    const hasLowerCase = /[a-z]/.test(security.newPassword);
    const hasNumbers = /\d/.test(security.newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(security.newPassword);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      showMessage('error', 'Password must contain uppercase, lowercase, number, and special character');
      return;
    }

    setIsLoading(true);

    try {
      // In a real app, you'd make an API call to change password
      // For now, we'll simulate the process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      showMessage('success', 'Password updated successfully!');
      setSecurity(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      console.error('Error changing password:', error);
      showMessage('error', 'Failed to change password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorToggle = async () => {
    setIsLoading(true);
    
    try {
      // In a real app, you'd make an API call to enable/disable 2FA
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSecurity(prev => ({
        ...prev,
        twoFactorEnabled: !prev.twoFactorEnabled
      }));
      
      showMessage('success', 
        security.twoFactorEnabled 
          ? 'Two-factor authentication disabled' 
          : 'Two-factor authentication enabled'
      );
    } catch (error) {
      console.error('Error toggling 2FA:', error);
      showMessage('error', 'Failed to update two-factor authentication. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to delete your account? This action cannot be undone and will permanently remove all your data.'
    );
    
    if (!confirmed) return;

    setIsLoading(true);

    try {
      // Get user ID from auth context or generate one
      const userId = user?.sub || userData?.sub || 'user123'; // Fallback for demo
      
      // Create backup before deletion
      try {
        await createBackup(userId);
        console.log('Backup created successfully');
      } catch (backupError) {
        console.warn('Failed to create backup, continuing with deletion:', backupError);
      }
      
      // Call the API to delete the account
      const result = await exportUserData(userId);
      
      if (result.success) {
        // Clear all localStorage data
        localStorage.clear();
        sessionStorage.clear();
        
        showMessage('success', 'Account deleted successfully');
        
        // Logout and redirect
        setTimeout(() => {
          logout();
        }, 2000);
      } else {
        throw new Error('Account deletion failed');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      showMessage('error', `Failed to delete account: ${error.message}`);
      setIsLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Update URL hash without triggering navigation
    window.history.replaceState(null, null, `#${tabId}`);
  };

  const handleDataExport = async () => {
    try {
      setIsLoading(true);
      
      // Get user ID from auth context or generate one
      const userId = user?.sub || userData?.sub || 'user123'; // Fallback for demo
      
      // Call the API to export user data
      const result = await exportUserData(userId);
      
      if (result.success) {
        // Download the data as a JSON file
        // Assuming userService.downloadUserData is available or replaced by a direct download
        // For now, we'll simulate a download
        const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `quila-user-data-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showMessage('success', 'Data export completed! Check your downloads folder.');
      } else {
        throw new Error('Data export failed');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      showMessage('error', `Failed to export data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: UserIcon },
    { id: 'role', label: 'Role Management', icon: UserIcon },
    { id: 'data', label: 'Data & Privacy', icon: LightbulbIcon }
  ];

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <button className={styles.backButton} onClick={() => window.history.back()}>
          Back
        </button>
        <h1>Account Settings</h1>
      </div>

      {/* Message */}
      {message.text && (
        <motion.div 
          className={`${styles.message} ${styles[message.type]}`}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
        >
          {message.type === 'success' && <CheckIcon size={16} />}
          {message.type === 'error' && <XIcon size={16} />}
          {message.text}
        </motion.div>
      )}

      <div className={styles.settingsContainer}>
        {/* Tabs */}
        <div className={styles.tabs}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <tab.icon size={20} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className={styles.tabContent}>
              <h3>Personal Information</h3>
              <div className={styles.formGroup}>
                <label>Full Name *</label>
                <input
                  type="text"
                  value={userProfile.fullName || ''}
                  onChange={(e) => handleProfileChange('fullName', e.target.value)}
                  placeholder="Enter your full name"
                  className={validation.fullName ? styles.inputError : ''}
                />
                {validation.fullName && (
                  <span className={styles.errorText}>{validation.fullName}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>Email *</label>
                <input
                  type="email"
                  value={userProfile.email || ''}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  placeholder="Enter your email"
                  className={validation.email ? styles.inputError : ''}
                />
                {validation.email && (
                  <span className={styles.errorText}>{validation.email}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={userProfile.phone || ''}
                  onChange={(e) => handleProfileChange('phone', e.target.value)}
                  placeholder="Enter your phone number"
                  className={validation.phone ? styles.inputError : ''}
                />
                {validation.phone && (
                  <span className={styles.errorText}>{validation.phone}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>High School *</label>
                <input
                  type="text"
                  value={userProfile.highSchool || ''}
                  onChange={(e) => handleProfileChange('highSchool', e.target.value)}
                  placeholder="Enter your high school name"
                  className={validation.highSchool ? styles.inputError : ''}
                />
                {validation.highSchool && (
                  <span className={styles.errorText}>{validation.highSchool}</span>
                )}
              </div>
              <div className={styles.formGroup}>
                <label>Graduation Year *</label>
                <select
                  value={userProfile.graduationYear || ''}
                  onChange={(e) => handleProfileChange('graduationYear', e.target.value)}
                  className={validation.graduationYear ? styles.inputError : ''}
                >
                  <option value="">Select graduation year</option>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {validation.graduationYear && (
                  <span className={styles.errorText}>{validation.graduationYear}</span>
                )}
              </div>
              <button 
                className={styles.saveButton} 
                onClick={handleSaveProfile}
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          )}

          {/* Role Management Tab */}
          {activeTab === 'role' && (
            <div className={styles.tabContent}>
              <h3>Role Management</h3>
              <div className={styles.formGroup}>
                <label>Current Role</label>
                <div className={styles.currentRole}>
                  <span className={styles.roleBadge}>
                    {userRole ? userRole.charAt(0).toUpperCase() + userRole.slice(1).replace('_', ' ') : 'No role selected'}
                  </span>
                </div>
              </div>
              
              <div className={styles.formGroup}>
                <label>Change Role</label>
                <p className={styles.roleDescription}>
                  Select a new role for your account. Note that changing to 'Counselor' will require verification.
                </p>
                <div className={styles.roleOptions}>
                  {availableRoles.map(role => (
                    <button
                      key={role.value}
                      className={`${styles.roleOption} ${userRole === role.value ? styles.roleOptionActive : ''}`}
                      onClick={() => handleRoleChange(role.value)}
                      disabled={isChangingRole || userRole === role.value}
                    >
                      {role.label}
                      {userRole === role.value && <span className={styles.currentIndicator}>Current</span>}
                    </button>
                  ))}
                </div>
                {isChangingRole && (
                  <div className={styles.loadingMessage}>
                    Changing role...
                  </div>
                )}
              </div>
              
              <div className={styles.roleInfo}>
                <h4>Role Descriptions</h4>
                <ul>
                  <li><strong>Student:</strong> Access to college planning tools, analytics, and resources</li>
                  <li><strong>Counselor:</strong> Access to counselor dashboard and student management tools (requires verification)</li>
                  <li><strong>District Student:</strong> District-specific student features and tools</li>
                  <li><strong>District Admin:</strong> District-level administrative access and management</li>
                </ul>
              </div>
            </div>
          )}

          {/* Data & Privacy Tab */}
          {activeTab === 'data' && (
            <div className={styles.tabContent}>
              <div className={styles.dataSection}>
                <h3>Data Export</h3>
                <p>Download a copy of all your data including profile, college list, activities, and analytics.</p>
                <button 
                  className={styles.exportButton}
                  onClick={handleDataExport}
                >
                  Export My Data
                </button>
              </div>
              
              <div className={styles.dangerZone}>
                <h4>Delete Account</h4>
                <p>This action will permanently delete your account and all associated data. This cannot be undone.</p>
                <button 
                  className={styles.dangerButton} 
                  onClick={deleteAccount}
                  disabled={isLoading}
                >
                  <TrashIcon size={16} />
                  {isLoading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSettings; 